import { MutationCache, QueryCache, QueryClient, replaceEqualDeep } from "@tanstack/react-query";
import { toastApiError } from "@/components/app/toast";
import { isSuppressedToast } from "./errorMessages";
import { etagOf } from "./etag";
import { isErrorHandled } from "./handledErrors";

function onError(error: unknown, silent?: boolean) {
  if (silent || isSuppressedToast(error)) return;
  toastApiError(error);
}

/** Shared TanStack Query client. Auth errors are suppressed (the session handles them). */
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    // Initial-load failures render inline (ErrorState); only background
    // refetch failures over already-rendered data surface as a toast.
    onError: (error, query) => onError(error, query.meta?.silent === true || query.state.data === undefined),
  }),
  mutationCache: new MutationCache({
    // Deferred one task so a caller that reports the error inline (ConfirmDialog)
    // can mark it handled first; then no duplicate toast is shown.
    onError: (error, _vars, _ctx, mutation) => {
      setTimeout(() => onError(error, mutation.meta?.silent === true || isErrorHandled(error)), 0);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // ETags live in a WeakMap keyed by the response object. Structural
      // sharing would copy a changed response into a new object and drop its
      // ETag, so responses that carry one replace the cache entry as-is.
      structuralSharing: (oldData, newData) =>
        etagOf(newData) !== undefined ? newData : replaceEqualDeep(oldData, newData),
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (error instanceof Error && "status" in error) {
          const status = (error as unknown as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}
